// src/admin/components/integrations/IntegrationHealthPanel.jsx
import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import DateTimeDisplay from '../common/DateTimeDisplay';
import StatusPill from '../../../shared/components/pill/StatusPill';
import { healthState } from './integrationStates';
import { fetchWithAuth } from '../../auth/fetchWithAuth';
import { API_URL } from '../../../config/env';

const CHECK_ICONS = {
  ok: 'bi-check-circle-fill text-success',
  warning: 'bi-exclamation-triangle-fill text-warning',
  failed: 'bi-x-circle-fill text-danger',
};

/**
 * Shows the last recorded health result and allows re-checking on demand.
 *
 * Reads the persisted result first so opening the panel costs no vendor calls. The failures this
 * surfaces are silent ones — a revoked client secret, a configured project that lost permission —
 * which otherwise only appear as a work report that never reached its ticket.
 */
export default function IntegrationHealthPanel({ detail }) {
  const [result, setResult] = useState(null);
  const [checking, setChecking] = useState(false);
  const [error, setError] = useState('');

  const runCheck = useCallback(async () => {
    setChecking(true);
    setError('');
    try {
      const res = await fetchWithAuth(
        `${API_URL}/v1/admin/integrations/${detail.id}/ticketing_health`,
        { cache: 'no-store' },
      );
      if (!res.ok) throw new Error('Health check failed');
      setResult(await res.json());
    } catch (err) {
      setError(err.message || 'Health check failed');
    } finally {
      setChecking(false);
    }
  }, [detail?.id]);

  // A fresh on-demand result wins; otherwise fall back to what the scheduled check recorded.
  const status = result?.status ?? detail?.health_status;
  const checks = result?.checks ?? detail?.health_detail?.checks ?? [];
  const checkedAt = result?.checked_at ?? detail?.health_checked_at;
  const badge = healthState(status);

  return (
    <div>
      <div className="d-flex align-items-center justify-content-between mb-3">
        <div>
          <StatusPill tone={badge.tone} icon={badge.icon}>{badge.label}</StatusPill>
          {checkedAt ? (
            <span className="text-muted small ms-2">
              Checked <DateTimeDisplay isoString={checkedAt} />
            </span>
          ) : (
            <span className="text-muted small ms-2">Never checked</span>
          )}
        </div>
        <button type="button" className="btn btn-outline-primary btn-sm" onClick={runCheck} disabled={checking}>
          {checking ? (
            <>
              <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true" />
              Checking…
            </>
          ) : (
            <>
              <i className="bi bi-heart-pulse me-1" />
              Check now
            </>
          )}
        </button>
      </div>

      {error && <div className="alert alert-danger py-2 small" role="alert">{error}</div>}

      {checks.length === 0 && !error && (
        <p className="text-muted small mb-0">
          No health result yet. Run a check, or wait for the scheduled one.
        </p>
      )}

      <ul className="list-unstyled mb-0">
        {checks.map((check) => (
          <li key={check.name} className="d-flex align-items-start mb-2">
            <i className={`bi ${CHECK_ICONS[check.status] || 'bi-dash-circle text-muted'} me-2 mt-1`} />
            <div>
              <div className="fw-semibold text-capitalize">{check.name}</div>
              <div className="text-muted small">{check.message}</div>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

IntegrationHealthPanel.propTypes = {
  detail: PropTypes.shape({
    id: PropTypes.number,
    health_status: PropTypes.string,
    health_checked_at: PropTypes.string,
    health_detail: PropTypes.object,
  }).isRequired,
};
